import { apiClient } from '@/services/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CreatePurgeRequest {
  email: string;
  includeOneDrive: boolean;
  removeAccess: boolean;
}

export interface PurgeReportRun {
  m365ReportRunId: number;
  email: string;
  includeOneDrive: boolean;
  removeAccess: boolean;
  isTest: boolean;
  reportStatus: string;
  reportResult?: string;
  reportStartDate?: string;
  reportEndDate?: string;
  createDt: string;
  createUser: string;
}

export interface EntraUser {
  userId: number;
  guid: string;
  objectId: string;
  upn?: string;
  email?: string;
  displayName?: string;
  employeeNum?: string;
  employeeDeptId?: string;
  employeeWindowsNtid?: string;
  managerEmpNum?: string;
  managerDeptId?: string;
  managerWindowsNtid?: string;
  orgHierarchy?: string;
  orgHierarchyDept?: string;
  lastCrawlDt: string;
  isActive: boolean;
  effEndDt?: string;
}

export interface EmployeeHierarchyUser {
  employeeHierarchyId: number;
  employeeFirstName?: string;
  employeeLastName?: string;
  employeeNum?: string;
  employeeEstNum?: string;
  employeeEmail?: string;
  employeeWindowsNtid?: string;
  employeePositionTitle?: string;
  employeeDeptId?: string;
  employeeType?: string;
  employeeStartDate?: string;
  employeeEndDate?: string;
  managerEmpId?: string;
  managerWindowsNtid?: string;
  managerFirstName?: string;
  managerLastName?: string;
  managerEmail?: string;
  managerPositionTitle?: string;
  managerDeptId?: string;
  loadDate?: string;
}

export interface EmployeeHierarchyChange extends EmployeeHierarchyUser {
  employeeHierarchyChangeId: number;
  change?: string;
}

export interface EmployeeHierarchyChangeSuggestion {
  employeeEmail: string;
  employeeFirstName?: string;
  employeeLastName?: string;
  employeeNum?: string;
  employeeWindowsNtid?: string;
  changeCount: number;
  lastChangeDate?: string;
}

export interface UserDirectoryFilterOptions {
  entraDepartmentIds: string[];
  employeeDepartmentIds: string[];
  employeeTypes: string[];
}

export interface UserResourceAccess {
  internalResourceId: string;
  resourceType: string;
  displayName?: string;
  url?: string;
  groupId?: string;
  primaryOwner?: string;
  secondaryOwner?: string;
  userRole?: string;
  relationshipSource?: string;
  lastCrawlDt?: string;
  lastUserActionDt?: string;
}

export interface EmployeeDepartmentAudit {
  auditEmployeeHierarchyDepartmentId: number;
  employeeHierarchyDepartmentId: number;
  employeeEmail?: string;
  employeeDepartment?: string;
  orgStructure?: string;
  orgStructureDepartments?: string;
  createDt?: string;
  lastUpdateDt?: string;
  effStartDt?: string;
  effEndDt?: string;
  guid?: string;
  loadDate?: string;
  type: string;
}

export interface UserDirectoryDetail {
  identifier: string;
  entraUser?: EntraUser;
  employeeHierarchyUser?: EmployeeHierarchyUser;
  resources: UserResourceAccess[];
  departmentAuditHistory: EmployeeDepartmentAudit[];
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

// ─── API functions ────────────────────────────────────────────────────────────

export const createPurgeRequest = async (dto: CreatePurgeRequest): Promise<void> => {
  await apiClient.post('/api/admin/users/sp-account-purge/report-runs', dto);
};

export const getPurgeReportRuns = async (
  page: number,
  pageSize: number
): Promise<PagedResult<PurgeReportRun>> => {
  const res = await apiClient.get<PagedResult<PurgeReportRun>>(
    '/api/admin/users/sp-account-purge/report-runs',
    { params: { page, pageSize } }
  );
  return res.data;
};

export const getEntraUsers = async (
  page: number,
  pageSize: number,
  search?: string,
  isActive?: boolean | null,
  departmentId?: string,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
): Promise<PagedResult<EntraUser>> => {
  const res = await apiClient.get<PagedResult<EntraUser>>('/api/admin/users/entra-users', {
    params: {
      page,
      pageSize,
      search: search || undefined,
      isActive: isActive ?? undefined,
      departmentId: departmentId || undefined,
      sortBy: sortBy || undefined,
      sortDirection: sortDirection || undefined,
    },
  });
  return res.data;
};

export const getEmployeeHierarchyUsers = async (
  page: number,
  pageSize: number,
  search?: string,
  employeeType?: string,
  departmentId?: string,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
): Promise<PagedResult<EmployeeHierarchyUser>> => {
  const res = await apiClient.get<PagedResult<EmployeeHierarchyUser>>('/api/admin/users/employee-hierarchy', {
    params: {
      page,
      pageSize,
      search: search || undefined,
      employeeType: employeeType || undefined,
      departmentId: departmentId || undefined,
      sortBy: sortBy || undefined,
      sortDirection: sortDirection || undefined,
    },
  });
  return res.data;
};

export const searchEmployeeHierarchyChanges = async (
  query: string
): Promise<EmployeeHierarchyChangeSuggestion[]> => {
  const res = await apiClient.get<EmployeeHierarchyChangeSuggestion[]>('/api/admin/users/employee-hierarchy-changes/search', {
    params: { q: query },
  });
  return res.data;
};

export const getEmployeeHierarchyChanges = async (
  email: string,
  page: number,
  pageSize: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
): Promise<PagedResult<EmployeeHierarchyChange>> => {
  const res = await apiClient.get<PagedResult<EmployeeHierarchyChange>>('/api/admin/users/employee-hierarchy-changes', {
    params: {
      email,
      page,
      pageSize,
      sortBy: sortBy || undefined,
      sortDirection: sortDirection || undefined,
    },
  });
  return res.data;
};

export const getUserDirectoryFilterOptions = async (): Promise<UserDirectoryFilterOptions> => {
  const res = await apiClient.get<UserDirectoryFilterOptions>('/api/admin/users/filter-options');
  return res.data;
};

export const getUserDirectoryDetail = async (identifier: string): Promise<UserDirectoryDetail> => {
  const res = await apiClient.get<UserDirectoryDetail>(`/api/admin/users/details/${encodeURIComponent(identifier)}`);
  return res.data;
};
